import { useQuery } from '@tanstack/react-query'

import { useInsightsContext } from '../context/InsightsContext'
import { FilterValueOption } from '../types/insights.types'

const LABELS_STALE_TIME_MS = 5 * 60_000
// The endpoint resolves up to 100 values per lookup (NEX-746); cap the request
// so an oversized selection can't 400 the whole hydration.
const MAX_LOOKUP_VALUES = 100

export type UseFilterValueLabelsResult = {
  /** Resolved `{ value, label }` for the requested values (a subset — values that
   * are not current options are omitted by the endpoint). */
  labels: FilterValueOption[]
  isLoading: boolean
}

/**
 * Resolves `{ value, label }` for an explicit set of already-known filter values
 * via the values endpoint's `?value=` lookup (NEX-746). Used to hydrate the
 * labels of a restored / URL-driven selection whose values may sit beyond the
 * first page of the paginated picker — so the pinned selection reads as labels,
 * not raw values, on load, without walking the keyset.
 *
 * Gated on `enabled`, on the host wiring `getFilterValues`, and on there being
 * values to resolve. The query key is order-independent, so re-selecting the
 * same values in a different order reuses the cache.
 */
export const useFilterValueLabels = (
  filter: string,
  values: string[],
  { enabled = true }: { enabled?: boolean } = {}
): UseFilterValueLabelsResult => {
  const { domain, service } = useInsightsContext()
  const getFilterValues = service.getFilterValues

  const lookup = [...values].sort().slice(0, MAX_LOOKUP_VALUES)

  const query = useQuery({
    queryKey: [`insights/${domain}/filters/${filter}/values`, { lookup }],
    queryFn: () => {
      // `enabled` already gates this on getFilterValues being wired; the guard is
      // kept because TypeScript can't narrow the optional method from `enabled`.
      /* istanbul ignore next -- unreachable: `enabled` gates queryFn on getFilterValues */
      if (!getFilterValues) {
        return Promise.reject(new Error('getFilterValues is not wired'))
      }

      return getFilterValues({ domain, filter, params: { value: lookup } })
    },
    enabled: enabled && Boolean(getFilterValues) && lookup.length > 0,
    staleTime: LABELS_STALE_TIME_MS,
    retry: 2,
  })

  return {
    labels: query.data?.data.data ?? [],
    isLoading: query.isLoading,
  }
}
